import { useRecoilValue } from 'recoil';
import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import {
  dataTrendPeriodAtom,
  formatMinutesToHoursAndMinutes,
  getWeekDatesOf,
  useAuthContext,
  useFetchCalendarDailyTrend,
  useFetchCalendarWeeklyTrend,
} from '../data-access';
import { CalendarDataWeeklyTrendWithData, CalendarDataDailyTrendWithData } from '@demind-inc/core';

interface UseCalendarTrendParams {
  date: Dayjs;
}

interface DiffInfo {
  value: number;
  formatted: string;
  color: string;
}

interface UseCalendarTrendReturn {
  dataTrendPeriod: string;
  isLoading: boolean;
  totalEvents: number;
  eventsDiff: DiffInfo;
  totalMeetings: number;
  meetingsDiff: DiffInfo;
  focusTime: string;
  focusTimeDiff: DiffInfo;
}

export const useCalendarTrend = ({ date }: UseCalendarTrendParams): UseCalendarTrendReturn => {
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const { user } = useAuthContext();

  const { calendarDailyTrend, isLoading: isDailyLoading } = useFetchCalendarDailyTrend({
    userId: user.userId,
    dataTrendId: user.dataTrendId!,
    date: date.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'daily',
  });

  const weekEdge = useMemo(() => {
    const week = getWeekDatesOf(date);
    return {
      startDate: week[0],
      endDate: week[week.length - 1],
    };
  }, [date]);

  const { calendarWeeklyTrend, isLoading: isWeeklyLoading } = useFetchCalendarWeeklyTrend({
    userId: user.userId,
    dataTrendId: user.dataTrendId!,
    startDate: weekEdge.startDate.format('YYYY-MM-DD'),
    endDate: weekEdge.endDate.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'weekly',
  });

  const isLoading = dataTrendPeriod === 'daily' ? isDailyLoading : isWeeklyLoading;

  const trend: CalendarDataDailyTrendWithData | CalendarDataWeeklyTrendWithData | undefined =
    dataTrendPeriod === 'daily' ? calendarDailyTrend : calendarWeeklyTrend;

  function getDiffInfo(
    diff: number | string,
    positiveColor: string,
    negativeColor: string,
    neutralColor: string
  ) {
    const value = Number(diff) || 0;

    return {
      value,
      formatted: `${Math.abs(value)}`,
      color: value > 0 ? positiveColor : value < 0 ? negativeColor : neutralColor,
    };
  }

  const totalEvents = trend?.numOfEvents?.current ?? 0;
  const eventsDiff = getDiffInfo(
    trend?.numOfEvents?.diff,
    'var(--color-green-base)',
    'var(--color-red-base)',
    'var(--color-gray-dark)'
  );

  const totalMeetings = trend?.numOfMeetings?.current ?? 0;
  const meetingsDiff = getDiffInfo(
    trend?.numOfMeetings?.diff,
    'var(--color-green-base)',
    'var(--color-red-base)',
    'var(--color-gray-dark)'
  );

  const focusTimeMin = trend?.deepWorkTimeMin?.current ?? 0;
  const { hours, remainingMinutes } = formatMinutesToHoursAndMinutes(focusTimeMin);
  const focusTime = `${hours > 0 ? hours + 'h ' : ''}${remainingMinutes}m`;
  const focusTimeDiff = getDiffInfo(
    trend?.deepWorkTimeMin?.diff,
    'var(--color-green-base)',
    'var(--color-red-base)',
    'var(--color-gray-dark)'
  );
  const { hours: diffHours, remainingMinutes: diffMinutes } = formatMinutesToHoursAndMinutes(
    Math.abs(Number(trend?.deepWorkTimeMin?.diff)) || 0
  );
  focusTimeDiff.formatted = `${diffHours > 0 ? diffHours + 'h ' : ''}${diffMinutes}m`;

  return {
    dataTrendPeriod,
    isLoading,
    totalEvents,
    eventsDiff,
    totalMeetings,
    meetingsDiff,
    focusTime,
    focusTimeDiff,
  };
};
